import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import { TabIndices } from '@/common/i-doc/i-doc.registry';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  getNotificationCatalog,
  setLegacyPushTypeEnabled,
  setNotificationScenarioEnabled,
} from '@services/notification.service.ts';
import { NotificationScenario, NotificationScenarioGroup } from '@/types/notification.ts';

const GROUP_LABELS: Record<NotificationScenarioGroup, string> = {
  appointments: 'Зустрічі',
  chat: 'Чат',
  connections: 'Підключення',
  surveys: 'Опитування',
  payments: 'Платежі',
  content: 'Контент',
  calendar: 'Календар',
  health_index: 'Індекс здоровʼя',
  expert_messages: 'Повідомлення експерта',
  system: 'Системні',
};

const RECIPIENT_LABELS: Record<NotificationScenario['recipient'], string> = {
  client: 'Клієнт',
  expert: 'Експерт',
  any: 'Будь-хто',
};

const CHANNEL_OPTIONS = [
  { value: 'feed', label: 'Стрічка + push' },
  { value: 'push_only', label: 'Лише push' },
];

const PREVIEW_LOCALES = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

type ExportFormat = 'csv' | 'md';

const channelLabel = (s: NotificationScenario) => (s.channels.feed ? 'стрічка + push' : 'лише push');

const downloadFile = (content: string, fileName: string, mime: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: `${mime};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
const mdCell = (value: string | number) => String(value).replace(/\|/g, '\\|').replace(/\n/g, ' ');

// Експорт для продакта: тексти однією мовою (обраною на сторінці), щоб таблицю можна було читати без коду.
const buildExport = (items: NotificationScenario[], locale: string, format: ExportFormat): string => {
  const header = ['Ключ', 'Назва', 'Група', 'Отримувач', 'Тригер', 'Канали', 'PushType', 'Заголовок', 'Текст', 'Увімкнено', 'Надіслано за 30 днів'];
  const rows = items.map((s) => [
    s.key,
    s.name,
    GROUP_LABELS[s.group] ?? s.group,
    RECIPIENT_LABELS[s.recipient] ?? s.recipient,
    s.trigger,
    channelLabel(s),
    s.pushType,
    s.preview[locale]?.title ?? '',
    s.preview[locale]?.body ?? '',
    s.enabled ? 'так' : 'ні',
    s.sentLast30Days,
  ]);

  if (format === 'csv') {
    // BOM — щоб Excel відкрив кирилицю без ручного вибору кодування.
    return '﻿' + [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
  }
  return [
    `# Каталог сповіщень (${locale})`,
    '',
    `| ${header.join(' | ')} |`,
    `| ${header.map(() => '---').join(' | ')} |`,
    ...rows.map((r) => `| ${r.map(mdCell).join(' | ')} |`),
    '',
  ].join('\n');
};

export const NotificationCatalogTab: React.FC = () => {
  const [items, setItems] = useState<NotificationScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [recipient, setRecipient] = useState('');
  const [channel, setChannel] = useState('');
  const [locale, setLocale] = useState('uk');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getNotificationCatalog());
    } catch {
      toastService.error('Не вдалося завантажити каталог сповіщень');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (group && s.group !== group) return false;
      if (recipient && s.recipient !== recipient) return false;
      if (channel === 'feed' && !s.channels.feed) return false;
      if (channel === 'push_only' && s.channels.feed) return false;
      if (!q) return true;
      const preview = s.preview[locale];
      return [s.key, s.name, s.trigger, preview?.title, preview?.body].some((v) => v?.toLowerCase().includes(q));
    });
  }, [items, search, group, recipient, channel, locale]);

  // PushType, вимкнені старим вимикачем `push` (колишній DevGlobalPushSettings) — він досі блокує FCM
  // для всіх сценаріїв цього типу, тож показуємо його тут, інакше вимкнений тип був би невидимим.
  const disabledPushTypes = useMemo(
    () => Array.from(new Set(items.filter((s) => !s.pushTypeEnabled).map((s) => s.pushType))),
    [items],
  );

  const handleToggle = async (scenario: NotificationScenario, enabled: boolean) => {
    setSavingKey(scenario.key);
    try {
      const updated = await setNotificationScenarioEnabled(scenario.key, enabled);
      setItems((prev) => prev.map((s) => (s.key === updated.key ? updated : s)));
      toastService.success(`${scenario.name}: ${enabled ? 'увімкнено' : 'вимкнено'}`);
    } catch {
      toastService.error('Не вдалося змінити статус сценарію');
    } finally {
      setSavingKey(null);
    }
  };

  const handleEnablePushType = async (pushType: string) => {
    try {
      await setLegacyPushTypeEnabled(pushType, true);
      toastService.success(`Push типу ${pushType} увімкнено`);
      load();
    } catch {
      toastService.error('Не вдалося увімкнути тип пушу');
    }
  };

  const handleExport = (format: ExportFormat) => {
    downloadFile(
      buildExport(filtered, locale, format),
      `notification-catalog-${locale}.${format}`,
      format === 'csv' ? 'text/csv' : 'text/markdown',
    );
  };

  return (
    <Box data-i-doc={TabIndices.ADMIN_NOTIFICATIONS_CATALOG}>
      <PageHint iDoc={TabIndices.ADMIN_NOTIFICATIONS_CATALOG} />
      <PageCard>
        <PageToolbar
          storageKey="filters:admin-notification-catalog"
          filters={[
            { id: 'search', type: 'text', label: 'Пошук', value: search, onChange: setSearch, flexGrow: 1 },
            {
              id: 'group',
              type: 'select',
              label: 'Група',
              value: group,
              onChange: setGroup,
              options: Object.entries(GROUP_LABELS).map(([value, label]) => ({ value, label })),
              minWidth: '170px',
            },
            {
              id: 'recipient',
              type: 'select',
              label: 'Отримувач',
              value: recipient,
              onChange: setRecipient,
              options: Object.entries(RECIPIENT_LABELS).map(([value, label]) => ({ value, label })),
              minWidth: '140px',
            },
            {
              id: 'channel',
              type: 'select',
              label: 'Канал',
              value: channel,
              onChange: setChannel,
              options: CHANNEL_OPTIONS,
              minWidth: '160px',
            },
          ]}
          onReset={() => { setSearch(''); setGroup(''); setRecipient(''); setChannel(''); }}
          extraActions={
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} onClick={() => handleExport('csv')} disabled={!filtered.length}>
                CSV
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={() => handleExport('md')} disabled={!filtered.length}>
                Markdown
              </Button>
            </Stack>
          }
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Мова превʼю:
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={locale} onChange={(_e, v) => v && setLocale(v)}>
            {PREVIEW_LOCALES.map((l) => (
              <ToggleButton key={l} value={l}>{l.toUpperCase()}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary">
            Сценаріїв: {filtered.length} з {items.length}
          </Typography>
        </Box>

        {disabledPushTypes.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Push вимкнено старим вимикачем за типом — для всіх сценаріїв цього типу FCM не надсилається (стрічка пишеться):
            <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              {disabledPushTypes.map((t) => (
                <Button key={t} size="small" variant="outlined" color="warning" onClick={() => handleEnablePushType(t)}>
                  Увімкнути {t}
                </Button>
              ))}
            </Stack>
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Сценарій</TableCell>
                <TableCell>Отримувач / тригер</TableCell>
                <TableCell>Канали</TableCell>
                <TableCell>Текст ({locale})</TableCell>
                <TableCell align="right">За 30 днів</TableCell>
                <TableCell align="center">Увімкнено</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Сценаріїв не знайдено
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((s) => {
                  const preview = s.preview[locale];
                  const fallback = !s.locales.includes(locale);
                  return (
                    <TableRow key={s.key} sx={{ opacity: s.enabled ? 1 : 0.55, verticalAlign: 'top' }}>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                          {s.key}
                        </Typography>
                        <Chip label={GROUP_LABELS[s.group] ?? s.group} size="small" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2" fontWeight={600}>{RECIPIENT_LABELS[s.recipient]}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.trigger}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip
                            label={channelLabel(s)}
                            size="small"
                            color={s.channels.feed ? 'primary' : 'default'}
                            variant={s.channels.feed ? 'filled' : 'outlined'}
                          />
                          <Tooltip title="data.type у FCM — за ним додаток робить перехід">
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.pushType}</Typography>
                          </Tooltip>
                          {!s.pushTypeEnabled && <Chip label="push вимкнено за типом" size="small" color="warning" />}
                          {s.channels.extraSocketEvents.map((e) => (
                            <Typography key={e} variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              socket: {e}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="body2" fontWeight={600}>{preview?.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{preview?.body}</Typography>
                        {fallback && (
                          <Typography variant="caption" color="warning.main">Немає перекладу — показується uk</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{s.sentLast30Days}</Typography>
                        {s.lastSentDate && (
                          <Typography variant="caption" color="text.secondary">ост. {s.lastSentDate}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={s.enabled}
                          disabled={savingKey === s.key}
                          onChange={(e) => handleToggle(s, e.target.checked)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PageCard>
    </Box>
  );
};
